import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export interface UserContext {
  /** Whether the user has access to multiple locations */
  isMultiLocation: boolean;
  /** The user's assigned location IDs */
  locationIds: string[];
  /** The user's single location ID (if single location user) */
  singleLocationId: string | null;
  /** Whether the user is an admin */
  isAdmin: boolean;
  /** Whether the user context is still loading */
  isLoading: boolean;
}

/**
 * Hook to detect user context for reports
 * Determines if user is single-location or multi-location
 * and provides location-based defaults
 */
export function useUserContext(): UserContext {
  const { user, isLoading } = useAuthStore();

  return useMemo(() => {
    if (isLoading || !user) {
      return {
        isMultiLocation: false,
        locationIds: [],
        singleLocationId: null,
        isAdmin: false,
        isLoading: true,
      };
    }

    const locations = user.locations || [];
    const locationIds = locations.map((loc) => loc.id);
    const isMultiLocation = locations.length > 1;
    const singleLocationId = locations.length === 1 ? locations[0].id : null;

    return {
      isMultiLocation,
      locationIds,
      singleLocationId,
      isAdmin: user.is_admin || false,
      isLoading: false,
    };
  }, [user, isLoading]);
}

